import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, Observable, shareReplay, switchMap, take, tap, throwError } from 'rxjs';
import { environment } from '../environments/environment';
import { callApi } from '../state-files/action';
import { selectApiData } from '../state-files/selectors';
import { ApiService } from './api.service';
import { UserService } from './user.service';
import { storeCachedPost, StoreCachedPostDeps } from './store-cached-post';
import { LocationTreeNode, markHasChildren } from '../shared/location-tree.utils';
import { storeBackedRequest } from './api-store-request';
import { buildLocationDropdownParams } from '../shared/location-dropdown-params';

/**
 * Request body for `POST /common/get_widget_summary` — the documents AI
 * summary banner. All three fields are required by the backend model; dates
 * travel as `yyyy-MM-dd` strings.
 */
export interface WidgetSummaryRequest {
  /** Customer the summary is scoped to (backend validates >= 1). */
  customer_id: number;
  /** User requesting the summary (backend validates >= 1). */
  user_id: number;
  /** Inclusive window start, `dd-MM-yyyy` (the format the live endpoint accepts). */
  report_start_date: string;
  /** Inclusive window end, `dd-MM-yyyy` (the format the live endpoint accepts). */
  report_end_date: string;
}

/** "Documents uploaded" card of the widget-summary response. */
export interface WidgetSummaryDocumentsUploadedCard {
  /** Documents uploaded in the requested window. */
  count?: number;
  /** Server-composed English title (the UI renders its own localized label). */
  title?: string;
  /** Server-composed English subtitle (ditto). */
  sub_title?: string;
}

/** "Document types" card — distinct types plus the leading type's share. */
export interface WidgetSummaryDocumentTypesCard {
  /** Distinct document types filed in the window. */
  type_count?: number;
  /** Name of the most-filed document type. */
  lead_doc_type?: string;
  /** Filings belonging to that leading type. */
  lead_doc_type_count?: number;
  /** All filings in the window (denominator for the lead share). */
  total_docs?: number;
  /** Server-composed English title (the UI renders its own localized label). */
  title?: string;
  /** Server-composed English subtitle (ditto). */
  sub_title?: string;
}

/** "Top uploader" card. */
export interface WidgetSummaryTopUploaderCard {
  /** Display name of the user who uploaded the most documents. */
  name?: string;
  /** Document count of that uploader. */
  document_count?: number;
  /** Server-composed English title (the UI renders its own localized label). */
  title?: string;
  /** Server-composed English subtitle (ditto). */
  sub_title?: string;
}

/** "Most-filed act" card. */
export interface WidgetSummaryMostFiledActCard {
  /** Name of the act with the most filed documents. */
  act_name?: string;
  /** Document count of that act. */
  document_count?: number;
  /** Server-composed English title (the UI renders its own localized label). */
  title?: string;
  /** Server-composed English subtitle (ditto). */
  sub_title?: string;
}

/** "Acts with no documents" card (names come from the sibling top-level list). */
export interface WidgetSummaryActsWithNoDocumentsCard {
  /** How many acts have no documents uploaded in the window. */
  act_count?: number;
  /** Server-composed English title (the UI renders its own localized label). */
  title?: string;
  /** Server-composed English subtitle (ditto). */
  sub_title?: string;
}

/** The five stat cards of the widget-summary payload. */
export interface WidgetSummaryCards {
  documents_uploaded?: WidgetSummaryDocumentsUploadedCard;
  document_types?: WidgetSummaryDocumentTypesCard;
  top_uploader?: WidgetSummaryTopUploaderCard;
  most_filed_act?: WidgetSummaryMostFiledActCard;
  acts_with_no_documents?: WidgetSummaryActsWithNoDocumentsCard;
}

/**
 * Payload of `get_widget_summary` as actually returned by the backend:
 * a `cards` object with the five stat cards plus the full name list of the
 * acts that have no documents. All fields optional so a partial response
 * degrades to fewer tiles instead of blanks.
 */
export interface WidgetSummaryResult {
  /** The five stat cards. */
  cards?: WidgetSummaryCards;
  /** Full names of the acts with no documents (the card holds only the count). */
  acts_with_no_documents?: string[];
}

/**
 * Standard RegTrack response envelope carried by the widget-summary endpoint;
 * the payload sits at `response.body.result`.
 */
export interface WidgetSummaryEnvelope {
  /** True when the call succeeded. */
  success: boolean;
  /** Empty string on success; message otherwise. */
  error: string;
  /** The typed payload. */
  result: WidgetSummaryResult;
}

/**
 * Shared application service that centralizes common API calls, cached store-backed lookups, and utility helpers used across the app.
 * @component
 * @selector common-service
 * @example
 *   CommonService methods are invoked by other components/services; no template selector is used for this injectable service.
 * @input none
 * @output none
 * @method getCalendarUserRoleDetails(params: any): Observable<HttpResponse<any>> - Fetches assigned calendar role details from the backend using a validated bearer token and HMAC-signed request headers.
 * @method getLocationDropdown_rjx(params: any): Observable<HttpResponse<any>> - Retrieves location dropdown data from NgRx store cache or dispatches an API call if the data is not yet available.
 * @method getLocationForFilters(params: any): Observable<HttpResponse<any>> - Loads location list data for filtering purposes from the backend using authenticated POST request headers.
 * @method getTaskApprovalLocationDropdown(params: any): Observable<HttpResponse<any>> - Fetches the task approval location dropdown list from the backend.
 * @method getUserRoleDetails(params: any): Observable<HttpResponse<any>> - Retrieves user role details from NgRx store cache or dispatches an API call if missing.
 * @method removeApostrophesFromFileName(file: File): File - Returns a new File instance with apostrophes removed from the filename, preserving file contents and type.
 * @lifecycle constructor(http: HttpClient, user: UserService, store: Store) - Injects HTTP client, user/token utilities, and NgRx store dependencies.
 */
@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private token: string | null = null;
  private apiBaseUrl = environment.API_BASE_URL;
  private apiBaseUrlV2 = environment.API_BASE_URL_V2;
  private fetchingKeys = new Set<string>();

  constructor(private http: HttpClient, private user: UserService, private store: Store, private api: ApiService, private actions$: Actions) { }

  /**
   * Collaborators for {@link storeCachedPost}; shares this service's in-flight key set.
   * @returns The store, actions stream and in-flight cache keys.
   */
  private cachedPostDeps(): StoreCachedPostDeps {
    return { store: this.store, actions$: this.actions$, fetchingKeys: this.fetchingKeys };
  }

  /**
  * Retrieves assigned calendar role details using authenticated API request headers and HMAC validation.
  * @example
  * etCalendarUserRoleDetails(params)
  * Observable<HttpResponse<any>>
  * @param {any} params - Request payload containing the data required to fetch calendar role details.
  * @returns {Observable<HttpResponse<any>>} Observable emitting the full HTTP response from the calendar roles endpoint.
  **/
  getCalendarUserRoleDetails(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrlV2}/management/get-assigned-calendar-roles`;
    return this.api.post(url, params);
  }


  /**
  * Fetches the location dropdown list by selecting cached data from the store and dispatching a POST API call if not present; returns an Observable that emits an HttpResponse whose body contains the unwrapped dropdown data.
  * @example
  * etLocationDropdown({ region: 'US' })
  * Observable<HttpResponse<any>> that emits { body: [...] } where body is the dropdown data
  * @param {{any}} {{params}} - Parameters sent as the POST payload to request the location dropdown list.
  * @returns {{Observable<HttpResponse<any>>}} Observable that emits an HttpResponse with the unwrapped response body containing the location list.
  **/
  getLocationDropdown_rjx(params: any): Observable<HttpResponse<any>> {
    const endpointKey = `/dropdown/get-location-list-drop-down`;

    return storeBackedRequest(this.store, endpointKey, params);
  }


  /**
  * Fetches a location dropdown list using the provided filter parameters.
  * @example
  * etLocationForFilters({ regionId: 1, searchText: 'New' })
  * HttpResponse<any>
  * @param {any} params - Filter parameters to be sent in the request body.
  * @returns {Observable<HttpResponse<any>>} An observable that emits the HTTP response containing the location list.
  **/
  getLocationForFilters(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/dropdown/get-location-list-drop-down`;
    return this.api.post(url, params);
  }

  /**
   * Fetches the task approval location dropdown data from the API using authenticated request headers.
   * @example
   * etTaskApprovalLocationDropdown(params)
   * Observable<HttpResponse<any>>
   * @param {any} params - Request payload used to generate the HMAC and sent to the dropdown API.
   * @returns {Observable<HttpResponse<any>>} An observable that emits the full HTTP response containing the dropdown data.
   */
  getTaskApprovalLocationDropdown(params: any): Observable<HttpResponse<any>> {
    const url = `${this.apiBaseUrl}/dropdown/task-approval-location-dropdown`;
    return this.api.post(url, params);
  }

  /**
   * Loads the location tree for the escalation screens (my / management escalation): the
   * task-approval locations for the user's role — executives and customer admins are scoped as
   * `PRA` — and the dashboard's current compliance type, with parent nodes flagged for the
   * DropDownTree's expanders.
   * @returns Observable emitting the tree (see {@link markHasChildren}), or `null` on a non-200
   * or empty response (logged). HTTP failures propagate to the subscriber.
   */
  getEscalationLocationTree(): Observable<LocationTreeNode[] | null> {
    // Dashboard filter stores the full word; the endpoint expects 'S' / 'I'.
    const role = this.user.getRole();
    const params = buildLocationDropdownParams(this.user, (role === 'EXCT' || role === 'CADMN') ? 'PRA' : role);
    return this.getTaskApprovalLocationDropdown(params).pipe(
      map((response) => {
        if (response.status !== 200 || !response.body) {
          console.error(`Unexpected response: Status = ${response.status}`);
          return null;
        }
        return markHasChildren(response.body.result[0].location_list as LocationTreeNode[]);
      })
    );
  }

  /**
   * Retrieves user role details from the common roles endpoint and returns the response as an observable HTTP response.
   * @example
   * etUserRoleDetails(params)
   * { body: userRoleDetails }
   * @param {any} params - Request payload used to fetch user role details.
   * @returns {Observable<HttpResponse<any>>} Observable emitting the unwrapped HTTP response containing user role details.
   **/
  getUserRoleDetails(params: any): Observable<HttpResponse<any>> {
    return storeCachedPost<any>(this.cachedPostDeps(), `/common/get-user-roles`, params);
  }

  /**
   * Session-lifetime cache of widget-summary responses, keyed by
   * `customer|start|end`. Reopening the documents AI banner with unchanged
   * date filters replays the cached response instead of issuing a duplicate
   * HTTP call; a transport error or a `success: false` envelope evicts its
   * key so the next open retries.
   */
  private widgetSummaryCache = new Map<string, Observable<HttpResponse<WidgetSummaryEnvelope>>>();

  /**
   * Fetches the documents AI-summary aggregates (common controller,
   * `get_widget_summary`) for the given customer and date window. The backend
   * binds this endpoint's model from the BODY, so the params are sent as a
   * JSON payload via {@link ApiService.post}, which applies the Bearer token
   * and computes the mandatory `X-Payload-HMAC` from the params object.
   *
   * Responses are cached per parameter set (see {@link widgetSummaryCache}) —
   * repeated calls with an identical window share ONE request/response for
   * the session, so toggling the banner does not spam the endpoint.
   * @param params - Customer id plus the inclusive `dd-MM-yyyy` date window.
   * @returns Observable emitting the full HTTP response; the payload sits at
   * `response.body.result` (see {@link WidgetSummaryResult} for the assumed shape).
   */
  getWidgetSummary(params: WidgetSummaryRequest): Observable<HttpResponse<WidgetSummaryEnvelope>> {
    const key = `${params.customer_id}|${params.report_start_date}|${params.report_end_date}|${params.user_id}`;
    let request$ = this.widgetSummaryCache.get(key);
    if (!request$) {
      const url = `${this.apiBaseUrl}/common/get_widget_summary`;
      request$ = this.api.post<WidgetSummaryEnvelope>(url, params).pipe(
        // Don't cache failures: a backend-declared failure (success: false)
        // or a transport error evicts the key so the next open retries live.
        tap((res) => {
          if (res.body && res.body.success === false) {
            this.widgetSummaryCache.delete(key);
          }
        }),
        catchError((err) => {
          this.widgetSummaryCache.delete(key);
          return throwError(() => err);
        }),
        shareReplay(1)
      );
      this.widgetSummaryCache.set(key, request$);
    }
    return request$;
  }

  removeApostrophesFromFileName(file: File): File {
    if (file.name.includes("'")) {
      const newFileName = file.name.replace(/'/g, '');
      return new File([file], newFileName, { type: file.type });
    }
    return file;
  }

}